import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Message, Header, Icon, Popup, Segment } from 'semantic-ui-react';
import { api } from '../api/apiClient';
import { useDropdownOptions } from '../hooks/useDropdownOptions';
import AgentCreationHistory from './AgentCreationHistory';

const toSelectOptions = (options = []) => options.map((option) => ({
    key: option.key || option.value,
    text: option.text,
    value: option.value
}));

const isBackupTypeAllowedForOs = (option, operatingSystem) => {
    const disallowed = option?.disallowedOperatingSystems;
    if (!Array.isArray(disallowed) || !operatingSystem) return true;
    return !disallowed.map(String).includes(String(operatingSystem));
};

const filterBackupTypesForOs = (backupTypeOptions, operatingSystem) => (
    backupTypeOptions.filter((option) => isBackupTypeAllowedForOs(option, operatingSystem))
);

const pickFirstValue = (options, fallback = '') => (
    options.length ? options[0].value : fallback
);

const INITIAL_VALUES = {
    operatingSystem: '',
    backupType: '',
    loginType: '',
    cloudName: '',
    environment: '',
    agentVersion: '',
    suffix: ''
};

const sectionSegmentStyle = {
    marginTop: '1rem',
    borderRadius: '10px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 3px 10px rgba(15, 23, 42, 0.04)',
    padding: '1rem 1.2rem',
    overflow: 'visible',
    position: 'relative'
};

const fieldGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '0 1rem'
};

const AgentCreation = () => {
    const { options: operatingSystemOptions } = useDropdownOptions('agent_operating_systems');
    const { options: backupTypeOptions } = useDropdownOptions('agent_backup_types');
    const { options: environmentOptions } = useDropdownOptions('agent_environments');

    const [view, setView] = useState('create');
    const [values, setValues] = useState(INITIAL_VALUES);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [versionLoading, setVersionLoading] = useState(false);
    const [versionHint, setVersionHint] = useState(null);
    const autoFilledVersionRef = useRef('');

    const availableBackupTypes = useMemo(
        () => filterBackupTypesForOs(backupTypeOptions, values.operatingSystem),
        [backupTypeOptions, values.operatingSystem]
    );

    const isBuildUnlocked = Boolean(
        (values.operatingSystem || '').trim()
        && (values.backupType || '').trim()
        && (values.cloudName || '').trim()
        && (values.environment || '').trim()
    );

    const sections = useMemo(() => [
        {
            key: 'agent',
            title: 'Agent Details',
            description: 'Choose the platform and backup type for the agent build.',
            fields: [
                {
                    name: 'operatingSystem',
                    label: 'Operating System',
                    type: 'select',
                    options: toSelectOptions(operatingSystemOptions),
                    required: true
                },
                {
                    name: 'backupType',
                    label: 'Backup Type',
                    type: 'select',
                    options: toSelectOptions(availableBackupTypes),
                    required: true
                },
                {
                    name: 'loginType',
                    label: 'Login Type',
                    type: 'readonly',
                    displayValue: 'Non-Silent',
                    required: false
                }
            ]
        },
        {
            key: 'cloud',
            title: 'Cloud',
            description: 'Cloud and POD the agent will connect to.',
            fields: [
                {
                    name: 'cloudName',
                    label: 'Cloud Name',
                    placeholder: 'e.g. acmecloud',
                    info: (
                        <span>
                            To find the Cloud Name, log in to the portal, navigate to the <strong>Support</strong> page, and check the <strong>Overview</strong> section.
                        </span>
                    ),
                    required: true
                },
                {
                    name: 'environment',
                    label: 'POD',
                    type: 'select',
                    options: toSelectOptions(environmentOptions),
                    required: true
                }
            ]
        },
        {
            key: 'build',
            title: 'Build',
            description: 'Version and naming for the generated agent.',
            fields: [
                { name: 'agentVersion', label: 'Agent Version', required: true }
            ]
        }
    ], [operatingSystemOptions, availableBackupTypes, environmentOptions]);

    const allFields = useMemo(
        () => sections.flatMap((section) => section.fields),
        [sections]
    );

    const canSubmit = allFields.every((field) => {
        if (!field.required || field.type === 'readonly') return true;
        return Boolean((values[field.name] || '').toString().trim());
    });

    // Keep selected values valid as dropdown config / OS compatibility changes.
    useEffect(() => {
        setValues((prev) => {
            const next = { ...prev };
            let changed = false;

            if (operatingSystemOptions.length) {
                const osValid = operatingSystemOptions.some((option) => option.value === next.operatingSystem);
                if (!osValid) {
                    next.operatingSystem = pickFirstValue(operatingSystemOptions);
                    changed = true;
                }
            }

            const allowedBackupTypes = filterBackupTypesForOs(backupTypeOptions, next.operatingSystem);
            if (allowedBackupTypes.length) {
                const backupValid = allowedBackupTypes.some((option) => option.value === next.backupType);
                if (!backupValid) {
                    next.backupType = pickFirstValue(allowedBackupTypes);
                    changed = true;
                }
            } else if (next.backupType) {
                next.backupType = '';
                changed = true;
            }

            if (environmentOptions.length) {
                const envValid = environmentOptions.some((option) => option.value === next.environment);
                if (!envValid && !next.environment) {
                    // Leave empty until user selects; do not force a default environment.
                } else if (!envValid && next.environment) {
                    next.environment = '';
                    changed = true;
                }
            }

            return changed ? next : prev;
        });
    }, [operatingSystemOptions, backupTypeOptions, environmentOptions]);

    useEffect(() => {
        if (!submitMessage) return undefined;
        const timer = setTimeout(() => setSubmitMessage(null), 3000);
        return () => clearTimeout(timer);
    }, [submitMessage]);

    // Prefill Agent Version from API Gateway when Cloud Name, POD, OS and Backup Type are set.
    useEffect(() => {
        const cloudName = (values.cloudName || '').trim();
        const environment = (values.environment || '').trim();
        const operatingSystem = (values.operatingSystem || '').trim();
        const backupType = (values.backupType || '').trim();

        if (!cloudName || !environment || !operatingSystem || !backupType) {
            setVersionLoading(false);
            setVersionHint(null);
            return undefined;
        }

        // Stay disabled/loading immediately so the field does not flash as editable
        // during the short debounce before the API call starts.
        setVersionLoading(true);
        setVersionHint(null);

        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const response = await api.unifiedAgent.getLatestVersion({
                    cloudName,
                    environment,
                    operatingSystem,
                    backupType
                });
                if (cancelled) return;
                const latestVersion = (response?.data?.data?.agentVersion || '').trim();
                if (!latestVersion) {
                    setVersionHint('No released version found for this OS and backup type.');
                    return;
                }
                setValues((prev) => {
                    const current = (prev.agentVersion || '').trim();
                    const previousAuto = autoFilledVersionRef.current;
                    const shouldReplace = !current || current === previousAuto;
                    if (!shouldReplace) return prev;
                    autoFilledVersionRef.current = latestVersion;
                    return { ...prev, agentVersion: latestVersion };
                });
                setVersionHint(`Latest released version: ${latestVersion}`);
                if (errors.agentVersion) {
                    setErrors((prev) => ({ ...prev, agentVersion: '' }));
                }
            } catch (err) {
                if (cancelled) return;
                setVersionHint(
                    err?.response?.data?.message || err?.message || 'Could not fetch the latest agent version'
                );
            } finally {
                if (!cancelled) setVersionLoading(false);
            }
        }, 400);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [values.cloudName, values.environment, values.operatingSystem, values.backupType]);

    const handleChange = (name, value) => {
        setValues((prev) => {
            const next = { ...prev, [name]: value };
            if (name === 'operatingSystem') {
                const allowed = filterBackupTypesForOs(backupTypeOptions, value);
                if (!allowed.some((option) => option.value === next.backupType)) {
                    next.backupType = pickFirstValue(allowed);
                }
            }
            return next;
        });
        if (name === 'agentVersion') {
            // Keep auto-fill from overwriting a version the user typed themselves.
            autoFilledVersionRef.current = '';
        }
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const buildClearedValues = () => {
        const operatingSystem = pickFirstValue(operatingSystemOptions);
        return {
            ...INITIAL_VALUES,
            operatingSystem,
            backupType: pickFirstValue(filterBackupTypesForOs(backupTypeOptions, operatingSystem))
        };
    };

    const handleClear = () => {
        autoFilledVersionRef.current = '';
        setVersionHint(null);
        setVersionLoading(false);
        setValues(buildClearedValues());
        setErrors({});
        setSubmitMessage(null);
        setSubmitError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = Object.fromEntries(
            Object.entries(values).map(([key, value]) => [key, (value || '').toString().trim()])
        );

        const nextErrors = {};
        allFields.forEach((field) => {
            if (field.required && !trimmed[field.name]) {
                nextErrors[field.name] = `${field.label} is required`;
            }
        });
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            return;
        }

        setErrors({});
        setSubmitError(null);
        setSubmitMessage(null);

        const payload = Object.fromEntries(
            allFields
                .filter((field) => field.type !== 'readonly')
                .map((field) => [field.name, trimmed[field.name]])
        );

        setSubmitting(true);
        try {
            const response = await api.unifiedAgent.createRequest(payload);
            setSubmitMessage(
                response?.data?.message || `Agent creation started for "${trimmed.cloudName}".`
            );
            autoFilledVersionRef.current = '';
            setVersionHint(null);
            setValues(buildClearedValues());
        } catch (err) {
            setSubmitError(
                err?.response?.data?.message || err?.message || 'Failed to submit agent creation request'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const renderField = (field) => {
        const error = errors[field.name] ? { content: errors[field.name], pointing: 'above' } : null;
        // Custom labels (with info icon) render the required asterisk manually so Semantic UI's
        // label:after asterisk does not sit after the icon and float out of alignment.
        const useCustomLabel = Boolean(field.info);
        return (
            <Form.Field key={field.name} required={field.required && !useCustomLabel}>
                <label
                    style={useCustomLabel ? {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        lineHeight: 1.2
                    } : undefined}
                >
                    <span>
                        {field.label}
                        {useCustomLabel && field.required ? (
                            <span style={{ color: '#db2828', marginLeft: '0.25rem' }}>*</span>
                        ) : null}
                    </span>
                    {field.info ? (
                        <Popup
                            content={field.info}
                            position="top left"
                            wide
                            trigger={(
                                <span
                                    style={{
                                        cursor: 'default',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        lineHeight: 1
                                    }}
                                >
                                    <Icon
                                        name="info circle"
                                        color="blue"
                                        style={{ cursor: 'default', margin: 0, fontSize: '1em' }}
                                        aria-label={`${field.label} help`}
                                    />
                                </span>
                            )}
                        />
                    ) : null}
                </label>
                {field.type === 'readonly' ? (
                    <Form.Input
                        fluid
                        readOnly
                        name={field.name}
                        value={field.displayValue}
                        input={{ style: { backgroundColor: '#f3f4f6', color: '#374151', cursor: 'default' } }}
                    />
                ) : field.type === 'select' ? (
                    <Form.Select
                        fluid
                        selection
                        name={field.name}
                        placeholder={`Select ${field.label}`}
                        options={field.options}
                        value={values[field.name]}
                        onChange={(e, { value }) => handleChange(field.name, value)}
                        disabled={submitting || !field.options?.length}
                        error={error}
                    />
                ) : (
                    <>
                        <Form.Input
                            fluid
                            name={field.name}
                            placeholder={field.placeholder || `Enter ${field.label}`}
                            value={values[field.name]}
                            onChange={(e, { value }) => handleChange(field.name, value)}
                            disabled={
                                submitting
                                || (field.name === 'agentVersion' && (!isBuildUnlocked || versionLoading))
                            }
                            loading={field.name === 'agentVersion' && isBuildUnlocked && versionLoading}
                            error={error}
                            input={field.name === 'agentVersion' && !isBuildUnlocked
                                ? { style: { backgroundColor: '#f3f4f6', color: '#9ca3af', cursor: 'not-allowed' } }
                                : undefined}
                        />
                        {field.name === 'agentVersion' && isBuildUnlocked && versionHint ? (
                            <div
                                style={{
                                    marginTop: '0.5rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.45rem',
                                    padding: '0.45rem 0.75rem',
                                    borderRadius: '6px',
                                    backgroundColor: versionHint.startsWith('Latest released version:')
                                        ? '#ecfdf5'
                                        : '#fff7ed',
                                    border: versionHint.startsWith('Latest released version:')
                                        ? '1px solid #a7f3d0'
                                        : '1px solid #fed7aa',
                                    color: versionHint.startsWith('Latest released version:')
                                        ? '#065f46'
                                        : '#9a3412',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    lineHeight: 1.3
                                }}
                            >
                                <Icon
                                    name={versionHint.startsWith('Latest released version:') ? 'check circle' : 'warning sign'}
                                    fitted
                                    style={{
                                        margin: 0,
                                        width: '1em',
                                        height: '1em',
                                        lineHeight: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        top: '1px'
                                    }}
                                />
                                <span style={{ lineHeight: 1.3 }}>{versionHint}</span>
                            </div>
                        ) : null}
                    </>
                )}
            </Form.Field>
        );
    };

    return (
        <div
            style={{
                padding: '1.5rem',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
            }}
        >
            <div
                style={{
                    marginBottom: '1.25rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between'
                }}
            >
                <div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: '1.35rem',
                            fontWeight: 700,
                            color: '#062D4D',
                            marginBottom: '0.2rem'
                        }}
                    >
                        <Icon name={view === 'history' ? 'history' : 'cogs'} color="blue" />
                        <span>{view === 'history' ? 'Custom Agent Creation History' : 'Custom Agent Creation'}</span>
                    </div>
                    <div style={{ marginTop: '0.35rem', fontWeight: 500, color: '#062D4D' }}>
                        {view === 'history'
                            ? 'View previously submitted agent creation requests'
                            : 'Enter the details to create a backup agent'}
                    </div>
                </div>
                <Button.Group size="small">
                    <Button
                        type="button"
                        active={view === 'create'}
                        color={view === 'create' ? 'blue' : undefined}
                        onClick={() => setView('create')}
                    >
                        <Icon name="plus" />
                        Create Agent
                    </Button>
                    <Button
                        type="button"
                        active={view === 'history'}
                        color={view === 'history' ? 'blue' : undefined}
                        onClick={() => setView('history')}
                    >
                        <Icon name="history" />
                        History
                    </Button>
                </Button.Group>
            </div>

            {view === 'history' ? <AgentCreationHistory /> : (
            <Form onSubmit={handleSubmit}>
                {sections.map((section, index) => (
                    <Segment
                        key={section.key}
                        raised
                        style={{ ...sectionSegmentStyle, zIndex: sections.length - index }}
                    >
                        <Header as="h4" style={{ margin: 0, marginBottom: '0.5rem', color: '#062D4D' }}>
                            {section.title}
                        </Header>
                        <div style={{ marginBottom: '0.75rem', color: '#4b5563', fontSize: '0.9rem' }}>
                            {section.description}
                        </div>
                        <div style={fieldGridStyle}>
                            {section.fields.map(renderField)}
                        </div>
                    </Segment>
                ))}

                {submitError && (
                    <Message negative onDismiss={() => setSubmitError(null)}>
                        <Message.Header>Submission failed</Message.Header>
                        <p>{submitError}</p>
                    </Message>
                )}

                {submitMessage && (
                    <Message positive onDismiss={() => setSubmitMessage(null)}>
                        <Message.Header>Agent creation started</Message.Header>
                        <p>{submitMessage}</p>
                    </Message>
                )}

                <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
                    <Button basic type="button" onClick={handleClear} disabled={submitting}>
                        <Icon name="erase" style={{ marginRight: '0.5rem' }} />
                        Clear
                    </Button>
                    <Button
                        color="green"
                        type="submit"
                        loading={submitting}
                        disabled={submitting || versionLoading || !canSubmit}
                    >
                        Create Agent
                        <Icon name="check" style={{ marginLeft: '0.5rem' }} />
                    </Button>
                </div>
            </Form>
            )}
        </div>
    );
};

export default AgentCreation;
